import { log } from '@/log'
import { ApiCreditcard } from '@/models/api/creditcard'
import { CreditCardType } from '@/models/app-config/behavior/types'
import { CreditcardRegisterResult, CreditcardService, CreditcardToBuildPaymentPayload } from '@/services/api/creditcard'
import { parametersWhichAreRequiredByEngineForSubscription } from '@/services/api/subscription/payment/parameters-which-are-required-by-engine-for-subscription'
import { ThreeDSecureExtraProfile } from '@spa/components/organisms/paid-membership/creditcard-extra-profile/extra-profile'
import { Either, left, right } from 'fp-ts/lib/Either'
import { Option } from 'fp-ts/lib/Option'
import { ApiClient } from '../../client'

import { ResponseError } from 'superagent'

const namespace: string = 'creditcard'

export class CreditcardServiceForSubscription extends CreditcardService {
  /**
   * 型がコンパイラで厳密に区別されるようにするための識別子
   */
  readonly _tag: 'for-subscription' = 'for-subscription'

  /**
   * @override 内部的にサブスクリプション用のパラメータを付与する
   * @see https://my-color.esa.io/posts/707
   * @see https://github.com/my-color/front/issues/5636
   */
  async get(id: string, query: object = {}): Promise<Either<Error, ApiCreditcard>> {
    return super.get(id, {
      ...parametersWhichAreRequiredByEngineForSubscription,
      ...query,
    })
  }

  /**
   * @override 内部的にサブスクリプション用のパラメータを付与する
   * @see https://my-color.esa.io/posts/707
   * @see https://github.com/my-color/front/issues/5636
   */
  async list(query: object = {}): Promise<Either<Error, ApiCreditcard[]>> {
    return super.list({
      ...parametersWhichAreRequiredByEngineForSubscription,
      ...query,
    })
  }

  async create(
    token: string,
    redirect: Option<URL>,
    extraProfile: Option<ThreeDSecureExtraProfile>
  ): Promise<Either<Error, ThreeDSecureCreditcardRegisterResult>> {
    // たまたまEngine向けプロキシと同じくtokenのみを引数とするが（たまたまというかKuroneko仕様に由来するのである程度は自然だが）
    // Engine向けプロキシと引数が変わりしだい、いつでも基底レベルで型を緩める、あるいはMixinにしてcreateだけは完全に独立で定義するといったことを検討してよい。
    try {
      const response = await ApiClient
        .post(`/api/user/creditcard/3dsecure/registration/`)
        .send({
          token,
          ...redirect.isSome() ?
          {
            redirect_url: redirect.value.toString(),
          } : {},
          ...extraProfile.isSome() ?
          {
            buyer_name_kanji: extraProfile.value.buyerNameKanji,
            buyer_email: extraProfile.value.buyerEmail,
            buyer_tel: extraProfile.value.buyerTel,
          } : {},
        })

      log.debug({ service: `${namespace}/create`, response })

      return right(new ThreeDSecureCreditcardRegisterResult(response.body.data.threeDAuthHtml))
    } catch (error) {
      log.error({ error })

      return left(error)
    }
  }

  /**
   * @override 内部的にサブスクリプション用のパラメータを付与する
   * @see https://my-color.esa.io/posts/707
   * @see https://github.com/my-color/front/issues/5636
   */
  buildPaymentPayload(paymentType: CreditCardType, creditcard: CreditcardToBuildPaymentPayload) {
    return {
      ...parametersWhichAreRequiredByEngineForSubscription,
      ...super.buildPaymentPayload(paymentType, creditcard),
    }
  }
}

export class ThreeDSecureCreditcardRegisterResult implements CreditcardRegisterResult {
  constructor(
    public readonly threeDAuthHtml: string
  ) {}
}

export type CreateForSubscriptionError<T extends
  CreateInternalErrorResponseData | CreateKuronekoErrorResponseData
> = Omit<ResponseError, 'response'> &{
  response: {
    body: {
      data: T
    }
  }
}

interface CreateInternalErrorResponseData {
  errorType: 'internal'
  error: any
}

interface CreateKuronekoErrorResponseData {
  errorType: 'kuroneko'
  error: {
    returnCode: 1
    errorCode: string
    creditErrorCode?: string
    returnDate: number
  }
}

export function isCreateForSubscriptionError(
  test: any,
  errorType: 'internal'
): test is CreateForSubscriptionError<CreateInternalErrorResponseData>

export function isCreateForSubscriptionError(
  test: any,
  errorType: 'kuroneko'
): test is CreateForSubscriptionError<CreateKuronekoErrorResponseData>

export function isCreateForSubscriptionError
  <T extends CreateInternalErrorResponseData | CreateKuronekoErrorResponseData>(
  test: any,
  errorType: T['errorType']
): test is CreateForSubscriptionError<T>  {
  return (
    typeof test === 'object' &&
    test !== null &&
    typeof test.response === 'object' &&
    test.response !== null &&
    typeof test.response.body === 'object' &&
    test.response.body !== null &&
    typeof test.response.body.data === 'object' &&
    test.response.body.data !== null &&
    'errorType' in test.response.body.data &&
    test.response.body.data.errorType === errorType
  )
}
